import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  InjectionToken,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  OiqFormInputComponent,
  OiqFormList,
  OiqFormListComponent,
  OiqFormSearchProvider,
} from '@oiq-ui-sdk/form';
import { catchError, debounceTime, Observable, of, switchMap, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CollectionTableFilterBaseComponent } from './filter.component';
import { CollectionFilterDefinition } from './filter.models';
import {
  CollectionFilterFormatPayloadSearch,
  CollectionFilterFromQuerySearch,
} from './filter.utils';

const _resultCache: { [id: string]: { params: any; collection: any[] } } = {};

@Component({
  selector: 'collection-filter-search',
  standalone: true,
  imports: [ReactiveFormsModule, OiqFormListComponent, OiqFormInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let _definition = definition();
    @if (_definition) {
      <!--      SELECTION-->
      @let _selection = selection();
      <oiq-form-list
        class="collection-filter-list relative block w-full border-t border-t-gray-4"
        [canClear]="_definition.search.canClear"
        [options]="_selection"
        [formControl]="internalSelection"
        [multi]="true"
        [searchable]="false"
        [virtual]="false"
        [configuration]="_definition.search.configuration"
        (event)="onSelectionChange($event)"></oiq-form-list>
      <!--      SEARCH-->
      <oiq-form-input
        class="block w-96"
        prefixIcon="search"
        [placeholder]="_definition.placeholder || _definition.label"
        (event)="onFilterChange($event)">
        <ng-template #tplRight>
          <span
            class="ph ph-spinner animate-spin text-base"
            [class.hidden]="!reading() && !searching()"></span>
        </ng-template>
      </oiq-form-input>
      <!--      LIST-->
      <oiq-form-list
        class="collection-filter-list block w-full border-t border-t-gray-4"
        [canClear]="_definition.search.canClear"
        [excludeSelected]="_definition.search.excludeSelected"
        [options]="result()"
        [formControl]="internalSearch"
        [searchable]="false"
        [virtual]="false"
        [multi]="_definition.search.multi"
        [configuration]="_definition.search.configuration"
        (event)="onListEvent($event)"></oiq-form-list>
    }
  `,
  styles: `
    collection-filter-search {
      @apply block;
      > .oiq-form-control-list {
        > fieldset {
          > .__container {
            @apply border-none !important;
          }
        }
      }
      .oiq-form-control-input {
        @apply p-2;
      }
      .oiq-ui-list {
        @apply border-none;
      }
    }
  `,
  encapsulation: ViewEncapsulation.None,
})
export class CollectionFilterSearchComponent extends CollectionTableFilterBaseComponent {
  internalSearch = new FormControl('');
  internalSelection = new FormControl<any[]>([]);
  search = new CollectionFilterSearchProvider();
  result = toSignal(this.search.result$);
  searching = toSignal(this.search.searching$);
  reading = signal<boolean>(false);
  private _list = new OiqFormList();
  value$ = this.internalSelection.valueChanges;
  selection$ = this.value$.pipe(
    tap(value => this.reading.set(true)),
    tap(value => {
      const _collection = this.search.readCache(value);
      if (_collection) {
        this.reading.set(false);
        this.selection.set(_collection);
      }
    }),
    debounceTime(750),
    switchMap(value => {
      const _hasSelection = Array.isArray(value) ? value.some(v => !!v) : !!value;
      return _hasSelection ? this.search.read(value) : of([]);
    }),
    tap(() => this.reading.set(false))
  );
  selection = signal<any>('');

  constructor() {
    super();
    this.internalSelection.setValue([]);

    this.selection$.subscribe(value => this.selection.set(value));
    effect(() => {
      const _value = this.value();
      const _definition = this.definition();
      this.search.definition = _definition;
      if (!_definition) {
        return;
      }
      const _normalized = CollectionFilterFromQuerySearch(_value, _definition, false);
      if (this.internalSelection.value === _normalized) {
        return;
      }
      this.internalSelection.setValue(_normalized || null);
      this.internalSearch.setValue(_normalized || null, {
        emitEvent: false,
      });
    });
  }

  // getPayload() {
  //   return CollectionFilterFormatPayloadSearch(this.internalControl.value, this.definition());
  // }

  getQuery() {
    return CollectionFilterFormatPayloadSearch(this.internalSelection.value, this.definition());
  }

  onFilterChange(value: { type: string; data?: any }): void {
    switch (value.type) {
      case 'change':
        this.search.term$.next(value.data);
    }
  }

  onSelectionChange(value: { type: string; data?: any }): void {
    switch (value.type) {
      case 'change':
        this.handleValueChanged();
    }
  }
  onListEvent(value: { type: string; data?: any }): void {
    switch (value.type) {
      case 'change':
        this.internalSelection.setValue(value.data);
        this.handleValueChanged();
    }
  }

  static fromQuery(query: any, definition: CollectionFilterDefinition): { [key: string]: any } {
    return CollectionFilterFromQuerySearch(query, definition, true);
  }
}

export class CollectionFilterSearchProvider extends OiqFormSearchProvider<any> {
  private _http = inject(HttpClient);
  private _registry = inject(COLLECTION_FILTER_SEARCH_REGISTRY);
  public definition: CollectionFilterDefinition;
  override search(query: string) {
    return this._search(query).pipe(
      map(response => (response.content ? response.content : response)),
      catchError(reason => {
        return of([]);
      })
    );
  }

  readCache(identity: any) {
    const _search = this.definition?.search;
    if (!_search) {
      return null;
    }
    let _params = {};
    let _url = '';
    switch (_search.source) {
      case 'http':
        _params = this.readHttpParams(identity);
        _url = this.readHttpUrl(identity);
        break;
      case 'registry':
        _params = identity;
        _url = _search.registry;
        break;
      default:
        return null;
    }
    if (!_params && !_url) {
      return null;
    }
    const _cached = _resultCache[_url];
    if (_cached && JSON.stringify(_cached.params) === JSON.stringify(_params)) {
      return _cached.collection;
    }
    return null;
  }

  override read(identity: any) {
    return this._read(identity).pipe(
      map(response => {
        const _collection = response.content ? response.content : response;
        const _normalized = Array.isArray(_collection) ? _collection : [_collection];
        return this._filterByIdentity(_normalized, identity);
      }),
      tap(collection => {
        const _search = this.definition.search;
        let _params = {};
        let _url = '';
        switch (_search.source) {
          case 'http':
            _params = this.readHttpParams(identity);
            _url = this.readHttpUrl(identity);
            break;
          case 'registry':
            _params = identity;
            _url = _search.registry;
        }
        if (!_params && !_url) {
          return;
        }
        _resultCache[_url] = { params: _params, collection };
      }),
      catchError(reason => {
        return of(null);
      })
    );
  }

  /**
   * Narrows a hydrated read response down to the records that were actually
   * requested. `read` is used to resolve the currently selected items shown in
   * the selection (top) panel; some list endpoints (e.g. `law-firms`) ignore
   * the `ids` query param and return a full page instead of the requested
   * subset, which would otherwise leak unselected items into that panel.
   */
  private _filterByIdentity(collection: any[], identity: any): any[] {
    const _search = this.definition?.search;
    // Single-select reads resolve one record by path — nothing to narrow.
    if (!_search || !_search.multi) {
      return collection;
    }
    const _idField = _search.configuration?.id || 'id';
    const _ids = (
      Array.isArray(identity) ? identity : identity != null ? identity.toString().split(',') : []
    )
      .map(_id => (_id == null ? '' : _id.toString().trim()))
      .filter(_id => !!_id);
    if (_ids.length === 0) {
      return [];
    }
    const _idSet = new Set(_ids);
    return collection.filter(_item => {
      const _id = _item ? _item[_idField] : null;
      return _id != null && _idSet.has(_id.toString());
    });
  }

  public readHttpParams(identity: any) {
    if (!this.definition) {
      return null;
    }
    const _search = this.definition.search;
    switch (_search.source) {
      case 'http':
        const _http = _search.http;
        const _params = _http.readParams || {};
        const _param = _http.readParam || 'ids';
        if (_param && _search.multi) {
          const _identity = identity
            ? Array.isArray(identity)
              ? identity
              : identity.toString().split(',')
            : [];
          _params[_param] = _identity
            .filter(id => !!id)
            .map(id => id.toString().trim())
            .filter(id => !!id)
            .join(',');
        }

        return { ..._params };
      default:
        return {};
    }
  }

  public readHttpUrl(identity: any) {
    if (!this.definition) {
      return null;
    }
    const _search = this.definition.search;
    switch (_search.source) {
      case 'http':
        const _http = _search.http;
        if (!_search.multi && !identity) {
          return '';
        }
        return (
          _http.readUrl + (!_search.multi ? '/' + encodeURIComponent(identity.toString()) : '')
        );
      default:
        return '';
    }
  }

  private _read(identity: any) {
    if (!this.definition) {
      return throwError(() => 'CollectionFilterSearchProvider: definition is not set');
    }
    const _search = this.definition.search;
    switch (_search.source) {
      case 'http':
        const _params = this.readHttpParams(identity);
        const _url = this.readHttpUrl(identity);

        return this._http
          .get<any>(_url, {
            params: {
              ..._params,
            },
          })
          .pipe(
            tap(response => {
              const _normalized = Array.isArray(response) ? response : [response];
              _resultCache[_url] = { params: _params, collection: _normalized };
            })
          );
      case 'registry':
        if (!_search.registry || !this._registry[_search.registry]) {
          return throwError(
            () => 'CollectionFilterSearchProvider: registry is not set or not found'
          );
        }
        const _registry = this._registry[_search.registry];
        return _registry.read(identity, this.definition);
      default:
        return throwError(() => 'CollectionFilterSearchProvider: invalid read source');
    }
  }

  private _search(query: string) {
    if (!this.definition) {
      return throwError(() => 'CollectionFilterSearchProvider: definition is not set');
    }
    const _search = this.definition.search;

    switch (_search.source) {
      case 'http':
        const _http = _search.http;
        const _params = _http.searchParams || {};
        if (_http.searchParam) {
          _params[_http.searchParam] = query;
        }
        return this._http.get<any>(
          _http.searchUrl + (_http.isTypeahead ? '/' + encodeURIComponent(query) : ''),
          {
            params: {
              ..._params,
            },
          }
        );
      case 'registry':
        if (!_search.registry || !this._registry[_search.registry]) {
          return throwError(
            () => 'CollectionFilterSearchProvider: registry is not set or not found'
          );
        }
        const _registry = this._registry[_search.registry];
        return _registry.search(query, this.definition);
      default:
        return throwError(() => 'CollectionFilterSearchProvider: invalid search source');
    }
  }

  static fromQuery(query: any, definition: CollectionFilterDefinition): { [key: string]: any } {
    return CollectionFilterFromQuerySearch(query, definition, true) as { [key: string]: any };
  }
}

export const COLLECTION_FILTER_SEARCH_REGISTRY = new InjectionToken<{
  [key: string]: {
    search: (query: string, definition: CollectionFilterDefinition) => Observable<any>;
    read: (query: string, definition: CollectionFilterDefinition) => Observable<any>;
  };
}>('COLLECTION_FILTER_SEARCH_REGISTRY');
